import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowsCounterClockwise, Flask, CircleNotch, Warning } from "@phosphor-icons/react";

const LABELS = { transactions: "transactions", invoices: "invoices", bills: "bills", bookings: "bookings", supplies: "supplies", documents: "documents", customers: "customers" };

// Admin-only: wipe a demo business and restore the clean sample dataset. Never available on live businesses.
export function DemoResetButton({ business, onReset }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setOpen(true); setTyped(""); setPreview(null);
    try { const { data } = await api.get(`/businesses/${business.id}/demo/reset`); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "This business can't be reset."); setOpen(false); }
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${business.id}/demo/reset`, { confirm_name: typed });
      toast.success(data.rowan_message, { duration: 10000 });
      setOpen(false);
      onReset?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed. Nothing was changed."); }
    finally { setBusy(false); }
  };

  const ready = preview && typed.trim() === preview.confirm_with;
  return (
    <>
      <button onClick={openDialog} data-testid="demo-reset-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--bk-gold)", color: "var(--bk-accent)", background: "#FFF9EE" }}>
        <ArrowsCounterClockwise size={16} weight="bold" /> Reset demo data
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="demo-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--bk-text)" }}><Flask size={20} weight="duotone" color="var(--bk-gold)" /> Reset demo data</DialogTitle>
            <DialogDescription>Only this demo business is affected. Live customer businesses can never be reset from here.</DialogDescription>
          </DialogHeader>
          {!preview ? (
            <p className="text-sm inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}><CircleNotch size={16} className="animate-spin" /> Checking what will be replaced…</p>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl p-4" style={{ background: "#FFF9EE", border: "1px solid var(--bk-gold)" }}>
                <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-gold)" }}>Demo business to reset</p>
                <p className="font-display text-xl font-bold mt-1" style={{ color: "var(--bk-text)" }} data-testid="demo-reset-target">{preview.name}</p>
                <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>{preview.business_type} · sample set: {preview.vertical}</p>
              </div>
              <div>
                <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: "var(--bk-text)" }}><Warning size={15} weight="fill" color="var(--bk-energy)" /> This will delete and replace</p>
                <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="demo-reset-counts">
                  {Object.entries(preview.will_replace).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${LABELS[k]}`).join(" · ") || "an empty workspace"} — then restore a clean, realistic sample dataset. Anything you added during testing will be gone.
                </p>
              </div>
              <label className="block text-xs" style={{ color: "var(--bk-text-2)" }}>Type <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{preview.confirm_with}</span> to confirm
                <input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="demo-reset-confirm-input" placeholder={preview.confirm_with} className="w-full mt-1 rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
              </label>
              <div className="flex gap-2">
                <button onClick={run} disabled={!ready || busy} data-testid="demo-reset-run" className="flex-1 py-2.5 rounded-full text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--bk-energy)" }}>{busy ? "Resetting…" : "Reset this demo business"}</button>
                <button onClick={() => setOpen(false)} disabled={busy} data-testid="demo-reset-cancel" className="px-4 py-2.5 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>Cancel</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
